import { useState, useEffect } from "react";
import { API, apiCall } from "../api";
import { useAuth } from "../context/AuthContext";

// Landlord identity verification (selfie + official ID)
const submitVerification = async (files, token) => {
  const body = new FormData();
  body.append('id_front', files.idFront);
  body.append('selfie', files.selfie);
  if (files.idBack) body.append('id_back', files.idBack);
  const response = await fetch(`${API}/verification/submit`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${token}` },
    body,
  });
  const result = await response.json().catch(() => ({}));
  if (!response.ok) throw result;
  return result;
};

const VerificationFields = ({ files, setFiles }) => {
  const field = (key, label, capture, optional) => (
    <div>
      <label className="block text-sm font-bold text-amber-800 mb-2">
        {label} {optional && <span className="text-amber-600 font-normal">(Opcional)</span>}
      </label>
      <input
        type="file"
        accept="image/*"
        capture={capture}
        onChange={(e) => setFiles(prev => ({ ...prev, [key]: e.target.files[0] || null }))}
        className="w-full text-sm text-gray-700 file:mr-3 file:py-2 file:px-4 file:rounded-lg file:border-0 file:bg-amber-100 file:text-amber-800 file:font-semibold"
      />
      {files[key] && (
        <img src={URL.createObjectURL(files[key])} alt={label} className="mt-2 h-24 rounded-lg border border-amber-200 object-cover" />
      )}
    </div>
  );
  return (
    <div className="space-y-4">
      {field('idFront', 'Identificaci\u00f3n oficial (frente)', 'environment')}
      {field('idBack', 'Identificaci\u00f3n oficial (reverso)', 'environment', true)}
      {field('selfie', 'Selfie', 'user')}
      <p className="text-xs text-gray-600">Usamos estas fotos solo para confirmar que eres una persona real. No se muestran a otros usuarios.</p>
    </div>
  );
};

const VerificationGate = () => {
  const { user, token, updateUser } = useAuth();
  const [files, setFiles] = useState({ idFront: null, idBack: null, selfie: null });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [rejectionReason, setRejectionReason] = useState(null);

  useEffect(() => {
    apiCall('/verification/status', 'GET', null, token)
      .then((r) => {
        setRejectionReason(r.rejection_reason);
        if (r.verification_status !== user.verification_status) updateUser({ verification_status: r.verification_status });
      })
      .catch(() => {});
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!files.idFront || !files.selfie) {
      setError('Sube la foto de tu identificaci\u00f3n y una selfie.');
      return;
    }
    setLoading(true);
    setError('');
    try {
      const result = await submitVerification(files, token);
      updateUser({ verification_status: result.verification_status });
    } catch (err) {
      setError(err.detail || 'No se pudo enviar la verificaci\u00f3n');
    } finally {
      setLoading(false);
    }
  };

  if (user.verification_status === 'pending') {
    return (
      <div className="max-w-xl mx-auto bg-amber-50 border border-amber-200 rounded-2xl p-6 text-center">
        <h3 className="text-xl font-bold text-amber-800 mb-2">Verificaci&oacute;n en revisi&oacute;n</h3>
        <p className="text-gray-700">Te avisaremos en cuanto revisemos tus fotos. Mientras tanto puedes buscar inquilinos.</p>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="max-w-xl mx-auto space-y-5">
      <div className="bg-amber-50 border border-amber-200 rounded-2xl p-5">
        <h3 className="text-xl font-bold text-amber-800 mb-2">Verifica tu identidad para evaluar</h3>
        <p className="text-gray-700 text-sm">Para evaluar inquilinos necesitamos una foto de tu identificaci&oacute;n oficial (INE o pasaporte) y una selfie.</p>
        {user.verification_status === 'rejected' && rejectionReason && (
          <p className="mt-3 text-sm text-red-700 font-semibold">No pudimos verificarte: {rejectionReason}. Env&iacute;a nuevas fotos.</p>
        )}
      </div>
      <VerificationFields files={files} setFiles={setFiles} />
      {error && <div className="bg-red-50 border-l-4 border-red-500 text-red-700 px-4 py-3 rounded-lg font-semibold">{error}</div>}
      <button
        type="submit"
        disabled={loading}
        className="w-full bg-gradient-to-r from-amber-500 to-amber-600 text-white py-3 px-6 rounded-xl font-bold disabled:opacity-50"
      >
        {loading ? 'Enviando...' : 'Verificar mi identidad'}
      </button>
    </form>
  );
};

const AdminVerifications = () => {
  const { token } = useAuth();
  const [status, setStatus] = useState('pending_review');
  const [requests, setRequests] = useState([]);
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(false);

  const statusLabels = {
    pending_review: 'Por revisar',
    approved: 'Aprobada',
    rejected: 'Rechazada',
    superseded: 'Reemplazada',
  };
  const docLabels = { id_front: 'Identificaci\u00f3n (frente)', id_back: 'Identificaci\u00f3n (reverso)', selfie: 'Selfie' };

  const load = async () => {
    setLoading(true);
    try {
      setRequests(await apiCall(`/admin/verifications?status=${status}`, 'GET', null, token));
    } catch (err) {
      alert(err.detail || 'No se pudieron cargar las verificaciones');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); setSelected(null); }, [status]);

  const open = async (id) => {
    try {
      setSelected(await apiCall(`/admin/verifications/${id}`, 'GET', null, token));
    } catch (err) {
      alert(err.detail || 'No se pudo abrir la solicitud');
    }
  };

  const approve = async () => {
    await apiCall(`/admin/verifications/${selected.id}/approve`, 'POST', null, token);
    setSelected(null);
    load();
  };

  const reject = async () => {
    const reason = window.prompt('Motivo del rechazo (lo ver\u00e1 el casero):');
    if (reason === null) return;
    await apiCall(`/admin/verifications/${selected.id}/reject`, 'POST', { reason }, token);
    setSelected(null);
    load();
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h3 className="text-xl font-bold text-amber-800">Verificaciones de caseros</h3>
        <select value={status} onChange={(e) => setStatus(e.target.value)} className="border-2 border-amber-200 rounded-xl px-3 py-2 font-medium">
          <option value="pending_review">Por revisar</option>
          <option value="approved">Aprobadas</option>
          <option value="rejected">Rechazadas</option>
          <option value="all">Todas</option>
        </select>
      </div>

      {loading ? (
        <p className="text-gray-600">Cargando...</p>
      ) : requests.length === 0 ? (
        <p className="text-gray-600">No hay solicitudes.</p>
      ) : (
        <div className="divide-y divide-amber-100 border border-amber-200 rounded-xl bg-white">
          {requests.map((r) => (
            <button key={r.id} onClick={() => open(r.id)} className="w-full text-left px-4 py-3 hover:bg-amber-50 flex flex-wrap justify-between gap-2">
              <span className="font-semibold text-gray-800">{r.landlord_name} <span className="font-normal text-gray-500">{r.landlord_email}</span></span>
              <span className="text-sm text-gray-600">{statusLabels[r.status] || r.status} &middot; {new Date(r.created_at).toLocaleDateString('es-MX')}</span>
            </button>
          ))}
        </div>
      )}

      {selected && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center p-4 z-[400]" onClick={() => setSelected(null)}>
          <div className="bg-white rounded-2xl p-6 max-w-3xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <h4 className="text-lg font-bold text-amber-800">{selected.landlord_name}</h4>
            <p className="text-sm text-gray-600 mb-4">{selected.landlord_email} &middot; {statusLabels[selected.status] || selected.status}</p>
            {selected.rejection_reason && <p className="text-sm text-red-700 mb-4">Motivo: {selected.rejection_reason}</p>}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
              {selected.documents.map((d) => (
                <a key={d.kind} href={d.url} target="_blank" rel="noopener noreferrer" className="block">
                  <img src={d.url} alt={docLabels[d.kind] || d.kind} className="w-full h-48 object-cover rounded-lg border border-amber-200" />
                  <span className="text-sm text-gray-700">{docLabels[d.kind] || d.kind}</span>
                </a>
              ))}
            </div>
            <div className="flex flex-wrap gap-3 justify-end">
              <button onClick={() => setSelected(null)} className="px-4 py-2 rounded-xl border border-gray-300 font-semibold">Cerrar</button>
              {selected.status !== 'rejected' && (
                <button onClick={reject} className="px-4 py-2 rounded-xl bg-red-600 text-white font-semibold">
                  {selected.status === 'approved' || selected.status === 'pending_review' ? 'Rechazar / revocar' : 'Rechazar'}
                </button>
              )}
              {selected.status !== 'approved' && (
                <button onClick={approve} className="px-4 py-2 rounded-xl bg-emerald-600 text-white font-semibold">Aprobar</button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export { submitVerification, VerificationFields, VerificationGate, AdminVerifications };
